import { A } from '@adornis/base/env-info.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { getInstitutionWhatsappConfigRaw, InstitutionWhatsappConfig } from '../entities/institution-whatsapp-config.js';
import { getAllRequiredWhatsappTemplateNames, getWhatsappBodyParamCount } from '../entities/mail-templates.js';
import { NotificationSendLog } from '../entities/notification-send-log.js';
import { mongo } from '../server/mongo.js';
import { checkUrlButton, getUrlButtons } from '../whatsapp/template-checks.js';
import { getInstitutionByID } from './institution.js';

export type InstitutionWhatsappTemplateStatus = 'APPROVED' | 'MISSING' | (string & {});

@Entity()
export class InstitutionWhatsappLastSendError extends AdornisEntity {
  static override _class = 'InstitutionWhatsappLastSendError';

  @Field(type => String)
  message!: string;

  @Field(type => Date)
  timestamp!: Date;

  static override resolveID(entity: InstitutionWhatsappLastSendError): string {
    return `${new Date(entity.timestamp).toISOString()}:${entity.message}`;
  }
}

@Entity()
export class InstitutionWhatsappTemplateValidation extends AdornisEntity {
  static override _class = 'InstitutionWhatsappTemplateValidation';

  @Field(type => String)
  institutionID!: string;

  @Field(type => String)
  templateName!: string;

  @Field(type => String)
  mailTemplateType!: string;

  @Field(type => String)
  status!: InstitutionWhatsappTemplateStatus;

  @Field(type => [String])
  languagesFound: Maybe<string[]>;

  @Field(type => [String])
  bodyParams?: string[];

  @Field(type => Int)
  paramCountFound: Maybe<number>;

  @Field(type => Int)
  paramCountExpected: Maybe<number>;

  @Field(type => String)
  parameterFormat: Maybe<string>;

  @Field(type => String)
  headerTypeFound: Maybe<string>;

  @Field(type => String)
  headerTypeExpected: Maybe<string>;

  @Field(type => String)
  urlButtonFound: Maybe<string>;

  @Field(type => String)
  urlButtonExpected: Maybe<string>;

  @Field(type => String)
  problem: Maybe<string>;

  @Field(type => InstitutionWhatsappLastSendError)
  lastSendError: Maybe<InstitutionWhatsappLastSendError>;

  static override resolveID(entity: InstitutionWhatsappTemplateValidation): string {
    return `${entity.institutionID}:${entity.mailTemplateType}`;
  }
}

@Entity()
export class InstitutionWhatsappConfigValidationResult extends AdornisEntity {
  static override _class = 'InstitutionWhatsappConfigValidationResult';

  @Field(type => Boolean)
  credentialsOk!: boolean;

  @Field(type => String)
  credentialsError: Maybe<string>;

  @Field(type => [InstitutionWhatsappTemplateValidation])
  templates!: InstitutionWhatsappTemplateValidation[];
}

type MetaTemplate = {
  name?: unknown;
  status?: unknown;
  language?: unknown;
  components?: unknown;
  parameter_format?: unknown;
};

type MetaOwnedWhatsappBusinessAccount = {
  id?: unknown;
  name?: unknown;
};

type MetaResponse = {
  data?: MetaTemplate[];
  error?: {
    message?: unknown;
  };
  owned_whatsapp_business_accounts?: {
    data?: MetaOwnedWhatsappBusinessAccount[];
  };
  paging?: {
    cursors?: {
      after?: unknown;
    };
    next?: unknown;
  };
};

type MetaRequestResult = {
  ok: boolean;
  data?: MetaResponse;
  error?: string;
};

async function requestMeta(url: URL, accessToken: string): Promise<MetaRequestResult> {
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = (await response.json().catch(() => undefined)) as MetaResponse | undefined;

    if (!response.ok) {
      const metaMessage = data?.error?.message;
      return {
        ok: false,
        error:
          typeof metaMessage === 'string'
            ? metaMessage
            : `Meta Graph API antwortete mit HTTP ${response.status} ${response.statusText}`.trim(),
      };
    }

    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

async function requestMetaTemplates(initialUrl: URL, accessToken: string): Promise<MetaRequestResult> {
  const inventory: MetaTemplate[] = [];
  const seenAfterCursors = new Set<string>();
  let nextUrl: URL | undefined = initialUrl;

  for (let page = 0; nextUrl && page < 10; page++) {
    const result = await requestMeta(nextUrl, accessToken);
    if (!result.ok) return { ...result, data: { data: inventory } };

    const pageTemplates = result.data?.data ?? [];
    inventory.push(...pageTemplates);

    const paging = result.data?.paging;
    const next = typeof paging?.next === 'string' && paging.next ? paging.next : undefined;
    const after = typeof paging?.cursors?.after === 'string' ? paging.cursors.after : undefined;

    if (next) {
      try {
        nextUrl = new URL(next);
      } catch (error) {
        return {
          ok: false,
          data: { data: inventory },
          error: error instanceof Error ? error.message : String(error),
        };
      }
    } else if (after && pageTemplates.length === 200 && !seenAfterCursors.has(after)) {
      seenAfterCursors.add(after);
      nextUrl = new URL(initialUrl);
      nextUrl.searchParams.set('after', after);
    } else {
      nextUrl = undefined;
    }
  }

  return { ok: true, data: { data: inventory } };
}

type LatestWhatsappSendFailure = {
  message: string;
  timestamp: Date;
};

type MetaTemplateComponent = {
  type?: unknown;
  format?: unknown;
  text?: unknown;
};

function getTemplateComponents(template: MetaTemplate): MetaTemplateComponent[] {
  if (!Array.isArray(template.components)) return [];
  return template.components.filter(
    (component): component is MetaTemplateComponent => typeof component === 'object' && component !== null,
  );
}

function getBodyPlaceholderInfo(template: MetaTemplate): { count: number; hasNamedPlaceholders: boolean } {
  const bodyText = getTemplateComponents(template)
    .filter(component => component.type === 'BODY' && typeof component.text === 'string')
    .map(component => component.text as string)
    .join('\n');
  const numericIndices = new Set<number>();
  let hasNamedPlaceholders = false;

  for (const match of bodyText.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
    const placeholder = match[1]?.trim() ?? '';
    if (/^\d+$/.test(placeholder)) numericIndices.add(Number(placeholder));
    else hasNamedPlaceholders = true;
  }

  return {
    count: numericIndices.size > 0 ? Math.max(...numericIndices) : 0,
    hasNamedPlaceholders,
  };
}

function getHeaderType(template: MetaTemplate): string | null {
  const header = getTemplateComponents(template).find(component => component.type === 'HEADER');
  if (!header) return null;
  return typeof header.format === 'string' ? header.format : 'UNKNOWN';
}

async function getLatestWhatsappSendFailures(institutionID: string): Promise<Map<string, LatestWhatsappSendFailure>> {
  const collection = mongo().collection<EntityData<NotificationSendLog>>(NotificationSendLog._collectionName);
  const failures = await collection.raw
    .aggregate<{ _id: string; errorMessage?: string | null; timestamp: Date }>([
      { $match: { institutionID, channel: 'whatsapp', status: 'failed' } },
      { $sort: { timestamp: -1 } },
      {
        $group: {
          _id: '$mailTemplateType',
          errorMessage: { $first: '$errorMessage' },
          timestamp: { $first: '$timestamp' },
        },
      },
    ])
    .toArray();

  return new Map(
    failures.map(failure => [
      failure._id,
      {
        message: failure.errorMessage || 'Unbekannter WhatsApp-Fehler',
        timestamp: failure.timestamp,
      },
    ]),
  );
}

function buildTemplateValidation(
  institutionID: string,
  inventory: MetaTemplate[],
  activePluginIDs: ReadonlySet<string>,
  latestSendFailures: ReadonlyMap<string, LatestWhatsappSendFailure>,
): InstitutionWhatsappTemplateValidation[] {
  const expectedBaseUrl = A.absoluteUrl('/').replace(/\/+$/, '') + '/';
  const variantsByName = new Map<string, MetaTemplate[]>();
  for (const template of inventory) {
    if (typeof template.name !== 'string') continue;
    const variants = variantsByName.get(template.name) ?? [];
    variants.push(template);
    variantsByName.set(template.name, variants);
  }

  return getAllRequiredWhatsappTemplateNames().flatMap(
    ({
      templateName,
      mailTemplateType,
      bodyParams,
      bodyParamCount: legacyBodyParamCount,
      expectedHeader,
      urlButton,
    }) => {
      const urlButtonExpected =
        urlButton === 'root' ? expectedBaseUrl + '{{1}}' : urlButton === 'dynamic' ? 'dynamisch ({{1}})' : undefined;
      const bodyParamCount = getWhatsappBodyParamCount({
        name: templateName,
        bodyParams,
        bodyParamCount: legacyBodyParamCount,
      });
      const paymentPluginPrefix = 'plugin::payment::';
      if (mailTemplateType.startsWith(paymentPluginPrefix)) {
        const pluginNameEnd = mailTemplateType.indexOf('::', paymentPluginPrefix.length);
        if (pluginNameEnd === -1) return [];

        const pluginID = `payment::${mailTemplateType.slice(paymentPluginPrefix.length, pluginNameEnd)}`;
        if (!activePluginIDs.has(pluginID)) return [];
      }

      const variants = variantsByName.get(templateName) ?? [];
      const languagesFound = [
        ...new Set(variants.flatMap(variant => (typeof variant.language === 'string' ? [variant.language] : []))),
      ].sort();
      const deVariant = variants.find(variant => variant.language === 'de');
      const sendFailure = latestSendFailures.get(mailTemplateType);
      const lastSendError = sendFailure
        ? new InstitutionWhatsappLastSendError({ message: sendFailure.message, timestamp: sendFailure.timestamp })
        : undefined;

      if (variants.length === 0) {
        return [
          new InstitutionWhatsappTemplateValidation({
            institutionID,
            templateName,
            mailTemplateType,
            bodyParams,
            paramCountExpected: bodyParamCount,
            urlButtonExpected,
            status: 'MISSING',
            languagesFound,
            problem: 'Template fehlt im WhatsApp-Manager',
            lastSendError,
          }),
        ];
      }

      if (!deVariant) {
        const availableLanguages = languagesFound.length > 0 ? languagesFound.join(', ') : 'keine Sprachangabe';
        return [
          new InstitutionWhatsappTemplateValidation({
            institutionID,
            templateName,
            mailTemplateType,
            bodyParams,
            paramCountExpected: bodyParamCount,
            urlButtonExpected,
            status: 'MISSING',
            languagesFound,
            problem: `nur ${availableLanguages} vorhanden — Template muss in Sprache 'de' angelegt werden`,
            lastSendError,
          }),
        ];
      }

      const metaStatus = typeof deVariant.status === 'string' ? deVariant.status : 'UNKNOWN';
      const parameterFormat = typeof deVariant.parameter_format === 'string' ? deVariant.parameter_format : 'UNKNOWN';
      const { count: paramCountFound, hasNamedPlaceholders } = getBodyPlaceholderInfo(deVariant);
      const headerTypeFound = getHeaderType(deVariant);
      const buttons = getUrlButtons(deVariant.components);
      const urlButtonFound = buttons.find(button => button.url.includes('{{1}}'))?.url ?? buttons[0]?.url ?? null;
      const problems: string[] = [];
      const urlButtonProblem = checkUrlButton({
        expected: urlButton,
        buttons,
        expectedBaseUrl,
      });
      if (urlButtonProblem) problems.push(urlButtonProblem);

      if (metaStatus !== 'APPROVED') {
        problems.push(`Template in Sprache 'de' hat Status '${metaStatus}' — es muss APPROVED sein`);
      }
      if (bodyParamCount !== undefined && (parameterFormat !== 'POSITIONAL' || hasNamedPlaceholders)) {
        problems.push(
          hasNamedPlaceholders
            ? 'Template verwendet benannte Platzhalter statt {{1}}, {{2}}, … — der Versand mit Positionsparametern wird fehlschlagen'
            : `Parameterformat ist '${parameterFormat}' statt 'POSITIONAL' — der Versand mit Positionsparametern wird fehlschlagen`,
        );
      }
      if (bodyParamCount !== undefined && paramCountFound !== bodyParamCount) {
        problems.push(`BODY enthält ${paramCountFound} Positionsparameter, erwartet werden ${bodyParamCount}`);
      }
      if (expectedHeader !== undefined && headerTypeFound !== expectedHeader) {
        if (expectedHeader === null) {
          problems.push(`Unerwarteter HEADER vom Typ ${headerTypeFound} — dieses Template wird ohne Header versendet`);
        } else if (headerTypeFound === null) {
          problems.push(`HEADER fehlt — erwartet wird ${expectedHeader}`);
        } else {
          problems.push(`HEADER hat Format ${headerTypeFound}, erwartet wird ${expectedHeader}`);
        }
      }
      const problem = problems.length > 0 ? problems.join('; ') : undefined;

      return [
        new InstitutionWhatsappTemplateValidation({
          institutionID,
          templateName,
          mailTemplateType,
          status: metaStatus !== 'APPROVED' ? metaStatus : problem ? 'INVALID' : 'APPROVED',
          languagesFound,
          bodyParams,
          paramCountFound: bodyParamCount === undefined ? undefined : paramCountFound,
          paramCountExpected: bodyParamCount,
          parameterFormat,
          headerTypeFound: expectedHeader === undefined ? undefined : headerTypeFound,
          headerTypeExpected: expectedHeader === undefined ? undefined : expectedHeader,
          urlButtonFound,
          urlButtonExpected,
          problem,
          lastSendError,
        }),
      ];
    },
  );
}

function errorResult(
  institutionID: string,
  message: string,
  activePluginIDs: ReadonlySet<string>,
  latestSendFailures: ReadonlyMap<string, LatestWhatsappSendFailure>,
): InstitutionWhatsappConfigValidationResult {
  return new InstitutionWhatsappConfigValidationResult({
    credentialsOk: false,
    credentialsError: message,
    templates: buildTemplateValidation(institutionID, [], activePluginIDs, latestSendFailures),
  });
}

export const validateInstitutionWhatsappConfig = registerQuery({
  operationName: 'validateInstitutionWhatsappConfig',
  type: () => InstitutionWhatsappConfigValidationResult,
  params: [{ name: 'institutionID', type: () => String }],
  resolve:
    (institutionID: string) => async (_gqlFields: BaseQLSelectionSet<InstitutionWhatsappConfigValidationResult>) => {
      const user = await getCurrentUserServer();
      if (!user?.isSuperAdmin) throw new Error('Not authorized: SUPER_ADMIN required');

      const [institution, latestSendFailures] = await Promise.all([
        getInstitutionByID(institutionID)({ plugins: 1 }),
        getLatestWhatsappSendFailures(institutionID),
      ]);
      const activePluginIDs = new Set(institution?.plugins ?? []);
      const config = await getInstitutionWhatsappConfigRaw(institutionID);
      if (!config)
        return errorResult(
          institutionID,
          'Für diese Einrichtung ist keine WhatsApp-Konfiguration gespeichert.',
          activePluginIDs,
          latestSendFailures,
        );

      const version = config.apiVersion?.trim() || 'v20.0';
      const baseUrl = `https://graph.facebook.com/${encodeURIComponent(version)}`;
      const phoneUrl = new URL(`${baseUrl}/${encodeURIComponent(config.phoneNumberId)}`);
      const templatesUrl = new URL(`${baseUrl}/${encodeURIComponent(config.businessId)}/message_templates`);
      templatesUrl.searchParams.set('fields', 'name,status,language,components,parameter_format');
      templatesUrl.searchParams.set('limit', '200');

      const [phoneResult, templatesResult] = await Promise.all([
        requestMeta(phoneUrl, config.accessToken),
        requestMetaTemplates(templatesUrl, config.accessToken),
      ]);

      let templatesError: string | null = null;
      if (!templatesResult.ok) {
        const diagnosisUrl = new URL(`${baseUrl}/${encodeURIComponent(config.businessId)}`);
        diagnosisUrl.searchParams.set('fields', 'owned_whatsapp_business_accounts{id,name}');
        const diagnosisResult = await requestMeta(diagnosisUrl, config.accessToken);
        const candidates = (diagnosisResult.data?.owned_whatsapp_business_accounts?.data ?? []).flatMap(account =>
          typeof account.id === 'string' && typeof account.name === 'string' ? [`${account.name} (${account.id})`] : [],
        );
        const hint =
          diagnosisResult.ok && candidates.length > 0
            ? `Die eingetragene Business-ID ist offenbar eine Business-Manager-ID; stattdessen muss die WhatsApp-Business-Account-ID (WABA-ID) eingetragen werden. Gefundene WhatsApp-Business-Accounts: ${candidates.join(
                ', ',
              )}.`
            : 'Die Business-ID muss die WhatsApp-Business-Account-ID (WABA-ID) aus dem WhatsApp Manager sein, nicht die Meta-Business-Manager-ID.';
        templatesError = `Templates: ${templatesResult.error ?? 'Unbekannter Fehler'} ${hint}`;
      }

      const errors = [
        phoneResult.ok ? null : `Telefonnummer: ${phoneResult.error ?? 'Unbekannter Fehler'}`,
        templatesError,
      ].filter((message): message is string => Boolean(message));

      return new InstitutionWhatsappConfigValidationResult({
        credentialsOk: errors.length === 0,
        credentialsError: errors.length > 0 ? errors.join(' ') : undefined,
        templates: buildTemplateValidation(
          institutionID,
          templatesResult.data?.data ?? [],
          activePluginIDs,
          latestSendFailures,
        ),
      });
    },
});

export const getInstitutionWhatsappConfigsByIDs = registerQuery({
  operationName: 'getInstitutionWhatsappConfigsByIDs',
  type: () => [InstitutionWhatsappConfig],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) =>
      readEntitiesByIDs(InstitutionWhatsappConfig, ids, gqlFields),
});

export const subscribeInstitutionWhatsappConfigsByIDs = registerSubscription({
  operationName: 'subscribeInstitutionWhatsappConfigsByIDs',
  type: () => [InstitutionWhatsappConfig],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(InstitutionWhatsappConfig, ids, gqlFields),
});

export const getAllInstitutionWhatsappConfigs = registerQuery({
  operationName: 'getAllInstitutionWhatsappConfigs',
  type: () => [InstitutionWhatsappConfig],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) =>
      readAllEntities(InstitutionWhatsappConfig, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllInstitutionWhatsappConfigs = registerSubscription({
  operationName: 'subscribeAllInstitutionWhatsappConfigs',
  type: () => [InstitutionWhatsappConfig],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) =>
      watchAllEntities(InstitutionWhatsappConfig, gqlFields, { skip, limit, excludeIDs }),
});

export const getInstitutionWhatsappConfigByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getInstitutionWhatsappConfigsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeInstitutionWhatsappConfigByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<InstitutionWhatsappConfig>>(gqlFields: SelSet) =>
    id
      ? subscribeInstitutionWhatsappConfigsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null))
      : of(null);
